import React from 'react';
import './index.css';
import styles from './App.module.css';
import { BusLogoIcon } from './components/BusLogoIcon';
import { Countdown } from './components/Countdown';
import { LeadForm } from './components/LeadForm';
import { SEO } from './components/SEO';
import { useCountdown } from './hooks/useCountdown';
import { trackEvent } from './firebase';

// ─── Fecha de lanzamiento (90 días desde hoy) ───────────────────────
const LAUNCH_DATE = new Date('2026-08-01T00:00:00');

// ─── Features highlight ─────────────────────────────────────────────
const FEATURES = [
  'Tracking en tiempo real',
  'Notificaciones 5 min antes',
  'Tarifas actualizadas',
  'Tarifa adulto mayor',
  'Rutas interurbanas CR',
];

export default function App() {
  const timeLeft = useCountdown(LAUNCH_DATE);

  // 📊 Track page view al montar
  React.useEffect(() => {
    trackEvent('page_view', { page_title: 'Landing Coming Soon' });
  }, []);

  return (
    <main className={styles.page}>
      <SEO />
      {/* ── Fondos decorativos ── */}
      <div className={styles.bgDots} aria-hidden="true" />
      <div className={styles.ambOrb} aria-hidden="true" />
      <div className={styles.grnOrb} aria-hidden="true" />
      <MountainsBg />

      {/* ── Contenido ── */}
      <div className={styles.wrapper}>

        {/* Badge "muy pronto" */}
        <div className={styles.badge} aria-label="Estado del proyecto: muy pronto">
          <span className={styles.badgeDot} aria-hidden="true" />
          Muy Pronto · Costa Rica
        </div>

        {/* Logo + nombre */}
        <div className={styles.logoRow}>
          <BusLogoIcon size={clampSize()} />
        </div>

        <h1 className={styles.brand} aria-label="Pura Ruta">
          <span className={styles.brandPura}>Pura</span>
          <span className={styles.brandRuta}> Ruta</span>
        </h1>

        <p className={styles.tagline}>Tu bus. En tiempo real.</p>

        <p className={styles.desc}>
          La app que <strong>Costa Rica necesitaba</strong>. Seguí tu autobús
          en tiempo real, conocé las tarifas y recibí un aviso cuando tu bus
          esté a <strong>5 minutos de llegar</strong>.
        </p>

        {/* Bus animado */}
        <BusTrack />

        {/* Countdown */}
        <Countdown {...timeLeft} />

        {/* Features */}
        <ul className={styles.features} aria-label="Funcionalidades">
          {FEATURES.map((f) => (
            <li key={f} className={styles.feat}>
              <span className={styles.featDot} aria-hidden="true" />
              {f}
            </li>
          ))}
        </ul>

        {/* Formulario */}
        <section className={styles.formSection} aria-labelledby="form-heading">
          <p id="form-heading" className={styles.formLabel}>
            Sé de los primeros en saberlo
          </p>
          <LeadForm />
        </section>

        {/* Footer */}
        <footer className={styles.footer}>
          <span aria-label="Bandera de Costa Rica">🇨🇷</span>
          <span className={styles.madeIn}>Hecho con orgullo en Costa Rica</span>
        </footer>
      </div>
    </main>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────

function clampSize() {
  if (typeof window === 'undefined') return 64;
  return Math.max(48, Math.min(72, window.innerWidth * 0.1));
}

function BusTrack() {
  return (
    <div className={styles.busTrack} aria-hidden="true">
      <svg className={styles.busSvg} viewBox="0 0 36 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="1" width="32" height="10" rx="3" fill="#1D9E75" />
        <rect x="4" y="3" width="8" height="5" rx="1" fill="rgba(255,255,255,0.5)" />
        <rect x="16" y="3" width="14" height="5" rx="1" fill="rgba(255,255,255,0.5)" />
        <circle cx="8" cy="13" r="2" fill="#EF9F27" />
        <circle cx="26" cy="13" r="2" fill="#EF9F27" />
      </svg>
    </div>
  );
}

function MountainsBg() {
  return (
    <svg
      className={styles.mountains}
      viewBox="0 0 1440 400"
      preserveAspectRatio="xMidYMax meet"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M0,400 L0,280 L120,200 L240,260 L360,140 L480,220
           L600,80 L720,180 L840,120 L960,200 L1080,60
           L1200,160 L1320,100 L1440,180 L1440,400 Z"
        fill="#1D9E75"
      />
      <path
        d="M0,400 L0,320 L180,260 L360,300 L540,220 L720,280
           L900,240 L1080,290 L1260,250 L1440,300 L1440,400 Z"
        fill="#085041"
      />
    </svg>
  );
}
